export interface ParsedShipmentAddress {
    company: string;
    contactName: string;
    address1: string;
    address2: string;
    city: string;
    region: string;
    postalCode: string;
    phone: string;
    email: string;
}

// --- One-time drop-ship address paste parsing ------------------------------------------------
// A best-effort parse for a one-time drop-ship address (UPS builder ship-to + the plastics one-time
// shipping slot), never saved to a customer, so a rough parse the user verifies is acceptable here.
// (The shared parseAddressString is reserved for the deferred free-text→structured migration.) This
// variant also pulls out the contact name, phone, and email, which the shared parser drops.
const PASTE_PHONE_REGEX = /(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/;
const PASTE_EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;
// "City, ST 12345" / "City ST 12345-6789" — the line that ends a US address block.
const PASTE_CITY_STATE_ZIP_REGEX = /^(.*?)[,\s]+([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?\s*$/;
// A line starting with a number or "PO Box" is a street, not a recipient name.
const PASTE_STREET_START_REGEX = /^\s*(\d|p\.?\s*o\.?\s*box)/i;
// Whole-line country names we skip (this form is domestic-only; country isn't stored here).
const PASTE_COUNTRY_LINE_REGEX = /^(usa|u\.?s\.?a?\.?|united states(?: of america)?|canada|mexico)$/i;
// "Attn:" / "c/o" contact line — captured as the contact name, not a street line.
const PASTE_ATTN_REGEX = /^(attn|attention|c\/o|care of)\b[:.\s]*/i;
// A line carrying no digit at all: a person's name effectively never has one, while a stray
// address fragment almost always does. Used to spot an UNMARKED attention line.
const PASTE_NAME_LIKE_REGEX = /^[^\d]+$/;
// Secondary address lines (unit/suite), kept in address2; other stray lines are dropped.
const PASTE_SECONDARY_REGEX = /^(suite|ste\.?|unit|apt\.?|apartment|#|floor|fl\.?|bldg\.?|building|room|rm\.?|dept\.?|department|space|spc\.?|lot|dock|door|gate)\b/i;

/**
 * Best-effort parse of a pasted free-text address (e.g. copied from a PO) into the structured
 * fields, INCLUDING contact name / phone / email. Not authoritative — the user confirms it.
 * Strategy: phone + email are grabbed from any line; the City/ST/ZIP line is found bottom-up; the
 * first line is treated as the recipient/company name unless it looks like a street; the remaining
 * lines (minus phone/email-only and country lines) are the street. A bare person line sitting
 * between the company and the street ("DCI MARKETING" / "Tanya Wilfong" / "4435 Duraform Lane")
 * becomes the contact name even without an "Attn:" marker.
 */
export function parseShipmentAddress(raw: string): ParsedShipmentAddress {
    const result: ParsedShipmentAddress = {
        company: '', contactName: '', address1: '', address2: '', city: '', region: '', postalCode: '', phone: '', email: '',
    };
    const lines = (raw || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return result;

    // Email + phone can be on any line — capture the first of each (phone stored as digits so the
    // input's mask formats it cleanly).
    for (const line of lines) {
        if (!result.email) { const m = line.match(PASTE_EMAIL_REGEX); if (m) result.email = m[0]; }
        if (!result.phone) { const m = line.match(PASTE_PHONE_REGEX); if (m) result.phone = m[1].replace(/\D/g, ''); }
    }

    // Country line (skipped — domestic-only form).
    let countryLineIdx = -1;
    for (let i = 0; i < lines.length; i++) {
        if (PASTE_COUNTRY_LINE_REGEX.test(lines[i])) { countryLineIdx = i; break; }
    }

    // City / region / postal — scan bottom-up for "City ST 12345".
    let cityLineIdx = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
        if (i === countryLineIdx) continue;
        const m = lines[i].match(PASTE_CITY_STATE_ZIP_REGEX);
        if (m) {
            cityLineIdx = i;
            result.city = m[1].trim().replace(/,+$/, '').trim();
            result.region = m[2].toUpperCase();
            result.postalCode = m[3];
            break;
        }
    }

    // A line whose only content is a phone/email (once those are stripped) is not a street or name.
    const isContactOnly = (line: string): boolean =>
        line.replace(PASTE_EMAIL_REGEX, '').replace(PASTE_PHONE_REGEX, '').replace(/[,;|]/g, ' ').trim().length === 0;
    // Shippers append UPS accessorials with a slash ("…/LIFTGATE", "…/INSIDE DELIVERY"); drop a
    // trailing "/…" so it doesn't pollute the parsed name/contact. The user re-adds it if it mattered.
    const stripTrailingSlashNote = (s: string): string => s.replace(/\s*\/.*$/, '').trim();

    // First line → recipient/company name, unless it's a street or contact-only. Strip phone/email
    // and any trailing "/ACCESSORIAL" note ("Queen of Heaven Cemetery/LIFTGATE" → "Queen of Heaven
    // Cemetery").
    let startIdx = 0;
    if (!PASTE_STREET_START_REGEX.test(lines[0]) && cityLineIdx !== 0 && !isContactOnly(lines[0])) {
        result.company = stripTrailingSlashNote(lines[0].replace(PASTE_EMAIL_REGEX, '').replace(PASTE_PHONE_REGEX, ''))
            .replace(/[,;]+$/, '')
            .trim();
        startIdx = 1;
    }

    // Walk the lines between the name and the city line: capture an "Attn:/c/o" contact, drop
    // phone/email-only lines, and collect the rest as street candidates.
    const streetEnd = cityLineIdx === -1 ? lines.length : cityLineIdx;
    const candidateStreet: string[] = [];
    for (let i = startIdx; i < streetEnd; i++) {
        if (i === countryLineIdx) continue;
        const line = lines[i];
        if (isContactOnly(line)) continue;
        if (PASTE_ATTN_REGEX.test(line)) {
            if (!result.contactName) result.contactName = stripTrailingSlashNote(line.replace(PASTE_ATTN_REGEX, ''));
            continue;
        }
        candidateStreet.push(line);
    }

    // address1 = the first street-looking line (starts with a number / PO Box); if none do (e.g.
    // "One Microsoft Way"), fall back to the first candidate. address2 keeps only further address-
    // like lines (another street line or a "Suite/Unit/#…"), so stray instruction lines are dropped
    // rather than jammed into the address.
    const streetIdx = candidateStreet.findIndex((l) => PASTE_STREET_START_REGEX.test(l));
    const primaryIdx = streetIdx >= 0 ? streetIdx : 0;
    result.address1 = candidateStreet[primaryIdx] ?? '';
    const isAddressLike = (l: string): boolean =>
        PASTE_STREET_START_REGEX.test(l) || PASTE_SECONDARY_REGEX.test(l);
    result.address2 = candidateStreet
        .filter((_, i) => i !== primaryIdx)
        .filter(isAddressLike)
        .join(', ');

    // UNMARKED attention name: "COMPANY / person / street" is how most pasted blocks name the
    // person, with no "Attn:" to find them by. Only lines ABOVE the street are considered (an
    // instruction line normally trails the address), and only ones with no digit — that keeps
    // "Dock 3" or "Gate 4" out while letting "Tanya Wilfong" through. These lines used to be
    // dropped entirely, so a wrong guess costs one field to clear rather than losing anything.
    if (!result.contactName) {
        const person = candidateStreet
            .slice(0, primaryIdx)
            .find((l) => !isAddressLike(l) && PASTE_NAME_LIKE_REGEX.test(l));
        if (person) result.contactName = stripTrailingSlashNote(person).replace(/[,;]+$/, '').trim();
    }
    return result;
}
